import type { ReactNode } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button, Card, CardContent, CardHeader, CardTitle, cn } from '@jira-clone/ui'
import { getApiErrorMessage } from '../../../shared/utils/apiError'

interface WidgetFrameProps {
  title: string
  /** One line under the title. */
  description?: string
  /** Rendered at the right of the header (e.g. a "Log Time" button or a link). */
  action?: ReactNode
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
  children?: ReactNode
  className?: string
}

/**
 * Card shell shared by every widget. Owns the loading and error states so a
 * widget that fails shows an inline error on its own card (with a retry)
 * and the rest of the Overview keeps working.
 */
export function WidgetFrame({ title, description, action, isLoading, error, onRetry, children, className }: WidgetFrameProps) {
  return (
    <Card className={cn('h-full flex flex-col', className)}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
        <div className="min-w-0">
          <CardTitle className="text-lg font-bold">{title}</CardTitle>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </CardHeader>
      <CardContent className="flex-1">
        {isLoading ? (
          <WidgetSkeleton />
        ) : error ? (
          <div role="alert" className="flex flex-col items-start gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
              <span>{getApiErrorMessage(error, `load ${title.toLowerCase()}`)}</span>
            </div>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
              </Button>
            )}
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  )
}

export function WidgetSkeleton() {
  return (
    <div data-testid="widget-skeleton" className="space-y-3 animate-pulse">
      <div className="h-4 w-1/3 rounded bg-muted" />
      <div className="h-8 w-1/2 rounded bg-muted" />
      <div className="h-4 w-2/3 rounded bg-muted" />
    </div>
  )
}
